'use client';

import { useEffect, useState } from 'react';
import TermsAndConditions from './terms';
import PrivacyPolicy from './privacy';

export default function LegalDocument({ type }: { type: 'terms' | 'privacy' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-400 mb-4">
                Table of Contents
              </h3>

              {/* Privacy Policy TOC */}
              {type === "privacy" && (
                <div className="mb-6">
                  <button
                    onClick={() => scrollToSection('privacy-policy')}
                    className="w-full text-left font-semibold text-blue-900 dark:text-blue-400 mb-3 p-2 bg-white dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Privacy Policy
                  </button>
                  <ul className="space-y-1 ml-4">
                    {[
                      { id: 'tc-introduction', label: 'Introduction' },
                      { id: 'tc-comprehensive', label: 'Comprehensive Data' },
                      { id: 'tc-legal', label: 'Legal Bases & Purposes' },
                      { id: 'tc-data-sharing', label: 'Data Sharing' },
                      { id: 'tc-international', label: 'International Data Transfers' },
                      { id: 'tc-data', label: 'Data Retention' },
                      { id: 'tc-security', label: 'Security Measures' },
                      { id: 'tc-user-right', label: 'User Rights' },
                      { id: 'tc-cookies', label: 'Cookies & Tracking' },
                      { id: 'tc-children', label: 'Children\'s Privacy' },
                      { id: 'tc-policy-update', label: 'Policy Updates' },
                      { id: 'tc-contact', label: 'Contact Information' }
                    ].map((item, index) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 rounded transition-colors"
                        >
                          {index + 1}. {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Terms & Conditions TOC */}
              {type === "terms" && (
                <div>
                  <button
                    onClick={() => scrollToSection('terms-conditions')}
                    className="w-full text-left font-semibold text-blue-900 dark:text-blue-400 mb-3 p-2 bg-white dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Terms & Conditions
                  </button>
                  <ul className="space-y-1 ml-4">
                    {[
                      { id: 'tc-introduction', label: 'Introduction' },
                      { id: 'tc-agreement', label: 'Agreement to Terms' },
                      { id: 'tc-services', label: 'Our Services' },
                      { id: 'tc-intellectual', label: 'Intellectual Property' },
                      { id: 'tc-representation', label: 'User Representations' },
                      { id: 'tc-user', label: 'User Registration' },
                      { id: 'tc-productsf', label: 'Products & Services' },
                      { id: 'tc-return', label: 'Refunds' },
                      { id: 'tc-prohibited', label: 'Prohibited Activities' },
                      { id: 'tc-user-granted', label: 'User Granted' },
                      { id: 'tc-contributions', label: 'Contribution License' },
                      { id: 'tc-guidelines', label: 'Guidelines' },
                      { id: 'tc-mobile', label: 'Mobile Application' },
                      { id: 'tc-socialmedia', label: 'Social Media' },
                      { id: 'tc-third-party', label: 'Third Party Services' },
                      { id: 'tc-advertisers', label: 'Advertisers' },
                      { id: 'tc-services', label: 'Services Management' },
                      { id: 'tc-privacy', label: 'Privacy Policy' },
                      { id: 'tc-copyright', label: 'Copyright Infringement' },
                      { id: 'tc-term', label: 'Term, Suspension & Termination of Access' },
                      { id: 'tc-modification', label: 'Modification of Terms' },
                      { id: 'tc-governing', label: 'Governing Law' },
                      { id: 'tc-dispute', label: 'Dispute Resolution' },
                      { id: 'tc-corrections', label: 'Corrections, Amendments & Revisions to Platform Content' },
                      { id: 'tc-disclaimer', label: 'Disclaimer' },
                      { id: 'tc-limitations', label: 'Limitations of Liability & Exclusion of Damages' },
                      { id: 'tc-indemnification', label: 'Indemnification & Hold Harmless Obligations' },
                      { id: 'tc-user-data', label: 'User Data Retention, Deletion, & Portability' },
                      { id: 'tc-electronic', label: 'Electronic Communications, Transactions & Signatures' },
                      { id: 'tc-california', label: 'California Users & Residents' },
                      { id: 'tc-miscellaneous', label: 'Miscellaneous' },
                      { id: 'tc-contact', label: 'Contact Information' }
                    ].map((item, index) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 rounded transition-colors"
                        >
                          {index + 1}. {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 lg:p-8">
              <h1 className="text-3xl lg:text-4xl font-bold text-blue-900 dark:text-blue-400 text-center mb-4">
                {type === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 text-center max-w-2xl mx-auto mb-8">
                Comprehensive legal documentation for Habesha Network classified advertisements platform
              </p>

              {/* Privacy Policy Content */}
              {type === 'privacy' && <PrivacyPolicy />}

              {/* Terms & Conditions Content */}
              {type === 'terms' && <TermsAndConditions />}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}